import { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useUserData } from "./userContext";
import {
  attachment_listAll,
  attachment_listMissing,
} from "src/components/attachments/AttachmentFunctions";
import { asiakas_fetch } from "src/data/asiakas/AsiakasFunctions";
import userAuthClass from "src/data/auth/userAuthClass";

export const AttachmentsContext = createContext();

// if keikkaid is given, then we are in keikka mode, list only keikka attachments
// if ownerAsiakasId is given, then list all attachments for that owner
// if both are given, then list all attachments for that keikka
export const AttachmentsProvider = ({ ownerAsiakasId, keikkaId, children }) => {
  const { user, userAsiakas, token, auth } = useUserData();
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [attachmentList, setAttachmentList] = useState();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const [attachmentAuth, setAttachmentAuth] = useState(null);

  useEffect(() => {
    if (keikkaId > 0) {
      const asynk = async () => {
        setIsLoading(true);
        const _list = await attachment_listAll(keikkaId, token);
        setAttachmentList(_list);
        setIsLoading(false);
        setIsLoaded(true);
      };
      asynk();
      return;
    }
    if (!ownerAsiakasId) return;
    const asynk = async () => {
      setIsLoading(true);
      const _list = await attachment_listMissing(ownerAsiakasId, token);
      setAttachmentList(_list);
      setIsLoading(false);
      setIsLoaded(true);
    };
    asynk();
  }, [ownerAsiakasId, nonce]);

  // update attachmentAuth when selectedAttachment changes
  useEffect(() => {
    if (!selectedAttachment) return;
    const attachmentOwnerAsiakasId = selectedAttachment?.ownerAsiakasId;
    if (attachmentOwnerAsiakasId == userAsiakas.asiakasId) {
      setAttachmentAuth(auth);
    } else {
      asiakas_fetch(user, attachmentOwnerAsiakasId, token, null, null, true).then((data) => {
        const auth = new userAuthClass(user, userAsiakas);
        auth.init(data.globalAsiakas);
        setAttachmentAuth(auth);
      });
    }
  }, [selectedAttachment]);

  const getNextAttachment = () => {
    const index = attachmentList.findIndex(
      (att) => att.attachmentId === selectedAttachment.attachmentId
    );
    const nextIndex = index === attachmentList.length - 1 ? 0 : index + 1;
    return attachmentList[nextIndex];
  };
  const getPrevAttachment = () => {
    const index = attachmentList.findIndex(
      (att) => att.attachmentId === selectedAttachment.attachmentId
    );
    const prevIndex = index === 0 ? attachmentList.length - 1 : index - 1;
    return attachmentList[prevIndex];
  };

  const setAttachment = (attachment) => {
    // replace attachement in list
    const index = attachmentList.findIndex((att) => att.attachmentId === attachment.attachmentId);
    const newList = [...attachmentList];
    newList[index] = attachment;
    setAttachmentList(newList);
  };

  const reloadAttachmentList = () => setNonce((prev) => prev + 1);

  return (
    <AttachmentsContext.Provider
      value={{
        isLoaded,
        selectedAttachment,
        setSelectedAttachment,
        attachmentList,
        setAttachmentList,
        setAttachment,
        isLoading,
        reloadAttachmentList,
        nonce,
        getNextAttachment,
        getPrevAttachment,
        attachmentAuth,
      }}>
      {children}
    </AttachmentsContext.Provider>
  );
};

export const useAttachmentsData = () => useContext(AttachmentsContext);

AttachmentsProvider.propTypes = {
  children: PropTypes.node,
  ownerAsiakasId: PropTypes.number,
  keikkaId: PropTypes.number,
};
